import { isUndefined } from '@energiebespaarders/constants';
import { useIsMobile } from '@energiebespaarders/hooks';
import { Box, Button, Container, Flex, Icon, theme } from '@energiebespaarders/symbols';
import { Medium } from '@energiebespaarders/symbols/helpers';
import { ArrowLeft } from '@energiebespaarders/symbols/icons/line';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useContext, useMemo } from 'react';
import { useKeyPressEvent, useMeasure } from 'react-use';
import config from '~/config';
import type { AnswerType, OmniformQuestion } from '~/hooks/omniform/types';
import { QuestionAnswerType } from '~/hooks/omniform/types';
import type { WithFormState } from '~/hooks/omniform/useOmniform';
import useABTest from '~/hooks/useABTest';
import useEventTracker from '~/hooks/useEventTracker';
import { useMe } from '~/hooks/useMe';
import type { me_me_Customer, me_me_Lead } from '~/types/generated/me';
import { QuestionType } from '~/types/graphql-global-types';
import { IntakeTextVariablesContext } from '../CustomerIntake/IntakeTextVariablesContext';
import InputHelper from './inputHelpers';
import OmniformCard from './OmniformCard';
import renderQuestionInput from './OmniformInputs';
import OmniformQuestionHelper from './OmniformQuestionHelper';

interface QuestionCardProps {
  question: WithFormState<OmniformQuestion>;
  onAnswer: <T extends QuestionAnswerType>(
    questionKey: string,
    answerType: T,
    answer: AnswerType<T>,
    skipNetwork?: boolean,
  ) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onFinish?: () => void;
  isFinalQuestion: boolean;
  formKey: string;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  onAnswer,
  question,
  onPrevious,
  onNext,
  onFinish,
  isFinalQuestion,
  formKey,
}) => {
  const { me } = useMe<me_me_Lead | me_me_Customer>();
  const hasMarketingConsent = me?.consent.some(consent => consent.title === 'marketing');
  const isMobile = useIsMobile();
  const textVariables = useContext(IntakeTextVariablesContext);

  const isArrayAnswer = [
    QuestionAnswerType.BooleanArray,
    QuestionAnswerType.DateArray,
    QuestionAnswerType.NumberArray,
    QuestionAnswerType.StringArray,
  ].includes(question.answerType);
  const hasError = question.touched && Boolean(question.error);
  const isMissingAnswer =
    !question.isOptional &&
    (isUndefined(question.answer) || (isArrayAnswer && _.isEmpty(question.answer)));
  const isSubmitDisabled = hasError || isMissingAnswer;
  const isInputFocused = document.activeElement?.tagName === 'INPUT';

  useKeyPressEvent('ArrowLeft', isInputFocused ? null : onPrevious);
  useKeyPressEvent('ArrowRight', () => (isSubmitDisabled || isInputFocused ? null : onNext?.()));
  useKeyPressEvent('Enter', () => (isSubmitDisabled ? null : onNext?.()));

  const [measureRef, { width }] = useMeasure<HTMLDivElement>();

  const { getAbTestEntry } = useABTest();

  /* AB test flag for showing the marketing consent in checkbox or separate question at the end of the intake */
  const usingConsentCard = useMemo(
    () =>
      dayjs().isBefore('2025-01-01') && // disable in 2025 for now, TODO: remove this check when/if we want it to be permanent
      config.isProduction && // prevent flakiness in e2e tests
      !hasMarketingConsent &&
      getAbTestEntry('selfIntake')?.find(entry => entry.experiment === 'marketing-consent')
        ?.variant === 'marketing-consent-card',
    [getAbTestEntry, hasMarketingConsent],
  );

  const trackEvent = useEventTracker();

  const handleAnswerOnNext = () => {
    if (isSubmitDisabled) return;

    void trackEvent(
      'answer_intake_question',
      {
        question: question.key,
        answer: question.answer,
        form_key: formKey,
      },
      false,
    );

    onAnswer(question.key, question.answerType, question.answer, true);
    onNext?.();
    if (isFinalQuestion) onFinish?.();
  };

  return (
    <OmniformCard px={4} py={isMobile ? 2 : 4} animation="none" id={`omniform-card-${question.id}`}>
      <Flex flexWrap="wrap">
        <Box width={1}>
          <Flex justifyContent="space-between">
            {onPrevious ? <Icon icon={ArrowLeft} stroke="gray" onClick={onPrevious} /> : <Box />}
            {question.helpContentCMSId && (
              <OmniformQuestionHelper datoCmsKey={question.helpContentCMSId} iconOnly />
            )}
          </Flex>
        </Box>

        <Box width={1} pt={[2, 3, 5]}>
          <Container size="md" textAlign="center">
            <p style={{ fontSize: theme.type.scale[4], letterSpacing: '-0.025em' }}>
              <Medium>{question.question}</Medium>
            </p>
            <p style={{ whiteSpace: 'pre-line' }}>{question.description}</p>
            {question.helpContentCMSId && (
              <OmniformQuestionHelper datoCmsKey={question.helpContentCMSId} />
            )}
          </Container>
        </Box>

        <Box width={1} pt={[2, 2, 5]} px={[0, 2, 5, 10]} ref={measureRef}>
          <Container
            size={
              question.type === QuestionType.radio || question.type === QuestionType.checkbox
                ? 'fluid'
                : 'sm'
            }
          >
            {renderQuestionInput(question, onAnswer, {
              hideLabels: true,
              inputWidth: width,
              autoFocus: true,
            })}

            <InputHelper question={question} onAnswer={onAnswer} textVariables={textVariables} />
          </Container>
        </Box>

        <Box width={1} textAlign="center" py={[5, 5, 10]}>
          <Button
            bgColor="yellow"
            color="grayBlack"
            onClick={handleAnswerOnNext}
            disabled={isSubmitDisabled}
            type={isFinalQuestion ? 'submit' : 'button'}
            style={{ borderRadius: 3 }}
            py={2}
            px={10}
            mr={0}
            mb={0}
            id={`next-button-${question.key}`}
            data-testid="omniform-button"
          >
            {isFinalQuestion && !usingConsentCard ? 'Voltooien →' : 'Volgende →'}
          </Button>
        </Box>
      </Flex>
    </OmniformCard>
  );
};

export default QuestionCard;
